import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Spinner,
  Textarea,
  Typography,
} from "@material-tailwind/react";
import {useMemo, useState} from "react";
import {toast} from "react-toastify";
import {UserCircleIcon} from "@heroicons/react/24/outline";
import SelectInput from "../../system/select";
import {getProvinces} from "../../../services/province/province";
import {getProvinceCities} from "../../../services/city/city";
import {
  getAgentProfile,
  submitAgentProfileChanges,
  uploadAgentProfileMedia,
} from "../../../services/agent/agent_profile";
import {
  AGENT_PROFILE_FIELDS,
  buildAgentProfileChanges,
  collectPendingAgentProfileFields,
  createAgentProfileForm,
  validateAgentProfileForm,
} from "./agentProfileModel";
import {AgentProfileHistory} from "./AgentProfileHistory";
import {
  agentRequestFieldClass,
  AgentRequestFieldLabel,
  agentRequestInputProps,
  agentRequestPrimaryButtonClass,
} from "../agent_request/ui";

const MAX_PHOTO_SIZE = 10 * 1024 * 1024;
const MAX_VIDEO_SIZE = 25 * 1024 * 1024;
const PHOTO_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const VIDEO_MIME_TYPES = new Set(["video/mp4", "video/webm", "video/quicktime"]);

function PendingField({field, pendingFields}) {
  if (!pendingFields[field]) return null;
  return <Typography className="mt-1 text-xs font-medium text-amber-700">تغییر این بخش در انتظار بررسی است.</Typography>;
}

function FieldError({message}) {
  return message ? <Typography className="mt-1 text-xs text-rose-700">{message}</Typography> : null;
}

export function AgentProfileManagement({initialData}) {
  const [profileData, setProfileData] = useState(initialData);
  const [form, setForm] = useState(() => createAgentProfileForm(initialData.profile.content));
  const [errors, setErrors] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [introductionFile, setIntroductionFile] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [removeIntroduction, setRemoveIntroduction] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState(0);

  const profile = profileData.profile;
  const approved = profile.content;
  const pendingFields = useMemo(
    () => collectPendingAgentProfileFields(profileData.pending_requests),
    [profileData.pending_requests],
  );

  const updateForm = (field, value) => {
    setForm(current => ({...current, [field]: value}));
    setErrors(current => ({...current, [field]: undefined, location: field === "province_id" || field === "city_id" ? undefined : current.location}));
  };

  const reload = async () => {
    const response = await getAgentProfile();
    if (response?.status === 200) {
      setProfileData(response.data);
      setForm(createAgentProfileForm(response.data.profile.content));
      setPhotoFile(null);
      setIntroductionFile(null);
      setRemovePhoto(false);
      setRemoveIntroduction(false);
      setRefreshSignal(value => value + 1);
    }
  };

  const chooseMedia = (field, file) => {
    if (!file) return;

    const isPhoto = field === "photo";
    const allowedTypes = isPhoto ? PHOTO_MIME_TYPES : VIDEO_MIME_TYPES;
    const maximumSize = isPhoto ? MAX_PHOTO_SIZE : MAX_VIDEO_SIZE;
    if (!allowedTypes.has(file.type)) {
      toast.error(isPhoto
        ? "فرمت تصویر باید JPEG، PNG یا WebP باشد."
        : "فرمت ویدئو باید MP4، WebM یا MOV باشد.");
      return;
    }
    if (file.size > maximumSize) {
      toast.error(isPhoto
        ? "حجم تصویر باید حداکثر ۱۰ مگابایت باشد."
        : "حجم ویدئو باید حداکثر ۲۵ مگابایت باشد.");
      return;
    }
    if (isPhoto) {
      setPhotoFile(file);
      setRemovePhoto(false);
    } else {
      setIntroductionFile(file);
      setRemoveIntroduction(false);
    }
  };

  const uploadMedia = async (field, file) => {
    const response = await uploadAgentProfileMedia(field, file);
    if (response?.status !== 201) {
      const code = response?.data?.error;
      const messages = {
        media_too_large: "حجم فایل بیش از حد مجاز است.",
        unsupported_media: "فرمت فایل پشتیبانی نمی‌شود.",
        invalid_image: "محتوای فایل، یک تصویر معتبر نیست.",
        processed_image_too_large: "تصویر پس از پردازش هنوز بیش از حد بزرگ است.",
      };
      toast.error(messages[code] ?? "بارگذاری فایل انجام نشد.");
      return null;
    }
    return response.data.file.id;
  };

  const submit = async () => {
    const validationErrors = validateAgentProfileForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) {
      toast.error("اطلاعات مشخص‌شده را اصلاح کنید.");
      return;
    }

    setSubmitting(true);
    try {
      const changes = buildAgentProfileChanges(approved, form, pendingFields);

      if (!pendingFields.photo) {
        if (photoFile) {
          const fileId = await uploadMedia("photo", photoFile);
          if (!fileId) return;
          changes.photo_file_id = fileId;
        } else if (removePhoto && approved.photo_file_id !== null) {
          changes.photo_file_id = null;
        }
      }

      if (!pendingFields.introduction_file) {
        if (introductionFile) {
          const fileId = await uploadMedia("introduction_file", introductionFile);
          if (!fileId) return;
          changes.introduction_file_id = fileId;
        } else if (removeIntroduction && approved.introduction_file_id !== null) {
          changes.introduction_file_id = null;
        }
      }

      if (!Object.keys(changes).length) {
        toast.info("تغییری برای ارسال وجود ندارد.");
        return;
      }

      const response = await submitAgentProfileChanges({
        expected_profile_version: profile.version,
        changes,
      });

      if (response?.status === 201) {
        toast.success("درخواست تغییرات برای بررسی ارسال شد.");
        await reload();
        return;
      }

      if (response?.status === 409) {
        toast.error(response.data?.error === "profile_modified"
          ? "پروفایل هم‌زمان تغییر کرده است. اطلاعات تازه بارگذاری شد."
          : "یکی از بخش‌ها در انتظار بررسی است. اطلاعات تازه بارگذاری شد.");
        await reload();
        return;
      }

      const field = response?.data?.field;
      if (field) {
        setErrors(current => ({...current, [field]: "مقدار این بخش معتبر نیست."}));
      }
      toast.error("ارسال درخواست انجام نشد.");
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = Object.keys(pendingFields).length;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 py-2 sm:py-4">
      <Card className="overflow-hidden rounded-3xl border border-app-border bg-app-surface shadow-app">
        <CardHeader floated={false} shadow={false} className="m-0 rounded-none bg-app-primary px-5 py-4 text-app-on-primary sm:px-7">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><UserCircleIcon className="h-6 w-6"/></div>
              <div>
                <Typography variant="h5" className="font-bold text-app-on-primary">پروفایل حرفه‌ای من</Typography>
                <Typography className="mt-1 text-sm text-app-on-primary/70">اطلاعات تاییدشده برای کاربران نمایش داده می‌شود.</Typography>
              </div>
            </div>
            <span className="rounded-full bg-white/10 px-3 py-1.5 text-xs">نسخه {profile.version}</span>
          </div>
        </CardHeader>

        <CardBody className="p-4 sm:p-6">
          {pendingCount > 0 && (
            <Alert className="mb-5 rounded-xl border border-amber-200 bg-amber-50 text-amber-900">
              {pendingCount} بخش از پروفایل شما در انتظار بررسی است. تا اعلام نتیجه، مقدار تاییدشده فعلی نمایش داده می‌شود.
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className={agentRequestFieldClass}>
              <AgentRequestFieldLabel htmlFor="profile-name">نام</AgentRequestFieldLabel>
              <Input {...agentRequestInputProps} id="profile-name" value={form.name} maxLength={100} disabled={Boolean(pendingFields.name)} onChange={event => updateForm("name", event.target.value)}/>
              <PendingField field="name" pendingFields={pendingFields}/><FieldError message={errors.name}/>
            </div>
            <div className={agentRequestFieldClass}>
              <AgentRequestFieldLabel htmlFor="profile-family">نام خانوادگی</AgentRequestFieldLabel>
              <Input {...agentRequestInputProps} id="profile-family" value={form.family} maxLength={100} disabled={Boolean(pendingFields.family)} onChange={event => updateForm("family", event.target.value)}/>
              <PendingField field="family" pendingFields={pendingFields}/><FieldError message={errors.family}/>
            </div>
            <div className={agentRequestFieldClass}>
              <AgentRequestFieldLabel htmlFor="profile-speciality">تخصص</AgentRequestFieldLabel>
              <Input {...agentRequestInputProps} id="profile-speciality" value={form.speciality} maxLength={2000} disabled={Boolean(pendingFields.speciality)} onChange={event => updateForm("speciality", event.target.value)}/>
              <PendingField field="speciality" pendingFields={pendingFields}/><FieldError message={errors.speciality}/>
            </div>
            <div className={agentRequestFieldClass}>
              <AgentRequestFieldLabel htmlFor="profile-experience">سابقه کاری (سال)</AgentRequestFieldLabel>
              <Input {...agentRequestInputProps} id="profile-experience" type="number" min="0" value={form.experience} disabled={Boolean(pendingFields.experience)} onChange={event => updateForm("experience", event.target.value)}/>
              <PendingField field="experience" pendingFields={pendingFields}/><FieldError message={errors.experience}/>
            </div>
            <div className={agentRequestFieldClass}>
              <SelectInput stackedLabel label="استان" disabled={Boolean(pendingFields.location)} defaultValue={form.province_id} autoLoadService={getProvinces} onChange={value => {
                updateForm("province_id", value);
                updateForm("city_id", null);
              }} />
            </div>
            <div className={agentRequestFieldClass}>
              <SelectInput stackedLabel label="شهر" disabled={Boolean(pendingFields.location)} defaultValue={form.city_id} refreshSignal={form.province_id} autoLoadService={getProvinceCities} autoLoadServiceData={form.province_id} onChange={value => updateForm("city_id", value)}/>
              <PendingField field="location" pendingFields={pendingFields}/><FieldError message={errors.location}/>
            </div>
            <div className={`${agentRequestFieldClass} md:col-span-2`}>
              <AgentRequestFieldLabel htmlFor="profile-about">درباره من</AgentRequestFieldLabel>
              <Textarea {...agentRequestInputProps} id="profile-about" rows={6} value={form.about_me} maxLength={20000} disabled={Boolean(pendingFields.about_me)} onChange={event => updateForm("about_me", event.target.value)}/>
              <PendingField field="about_me" pendingFields={pendingFields}/><FieldError message={errors.about_me}/>
            </div>
          </div>

          <div className="mt-6 grid gap-4 border-t border-app-border pt-5 lg:grid-cols-2">
            <MediaField
              title={AGENT_PROFILE_FIELDS.photo.label}
              description="JPEG، PNG یا WebP تا ۱۰ مگابایت؛ خروجی بهینه WebP"
              accept="image/jpeg,image/png,image/webp"
              approvedId={approved.photo_file_id}
              pending={Boolean(pendingFields.photo)}
              file={photoFile}
              removed={removePhoto}
              onChoose={file => chooseMedia("photo", file)}
              onRemove={() => { setPhotoFile(null); setRemovePhoto(true); }}
            />
            <MediaField
              title={AGENT_PROFILE_FIELDS.introduction_file.label}
              description="MP4، WebM یا MOV تا ۲۵ مگابایت"
              accept="video/mp4,video/webm,video/quicktime"
              approvedId={approved.introduction_file_id}
              pending={Boolean(pendingFields.introduction_file)}
              file={introductionFile}
              removed={removeIntroduction}
              onChoose={file => chooseMedia("introduction_file", file)}
              onRemove={() => { setIntroductionFile(null); setRemoveIntroduction(true); }}
            />
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-app-border pt-5">
            <Typography className="text-xs text-app-text-muted">تغییرات پس از تایید مدیر روی سایت نمایش داده می‌شوند.</Typography>
            <Button className={agentRequestPrimaryButtonClass} disabled={submitting} onClick={submit}>
              {submitting ? <span className="flex items-center gap-2"><Spinner className="h-4 w-4"/>در حال ارسال</span> : "ارسال برای بررسی"}
            </Button>
          </div>
        </CardBody>
      </Card>

      <AgentProfileHistory refreshSignal={refreshSignal} onChanged={reload}/>
    </div>
  );
}

function MediaField({title, description, accept, approvedId, pending, file, removed, onChoose, onRemove}) {
  return (
    <div className={`rounded-2xl border p-4 ${pending ? "border-amber-200 bg-amber-50/60" : "border-app-border bg-app-raised"}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div><Typography className="font-semibold text-app-text">{title}</Typography><Typography className="mt-1 text-xs text-app-text-muted">{description}</Typography></div>
        {pending && <span className="rounded-full border border-amber-200 bg-white px-2 py-1 text-xs text-amber-800">در انتظار بررسی</span>}
      </div>
      <div className="mb-3 rounded-lg bg-app-muted px-3 py-2 text-sm text-app-text-muted">
        {file?.name ?? (removed ? "برای حذف ارسال می‌شود" : approvedId ? `فایل تاییدشده شماره ${approvedId}` : "فایل تاییدشده‌ای وجود ندارد")}
      </div>
      <div className="flex flex-wrap gap-2">
        <label className={`cursor-pointer rounded-lg bg-app-primary px-4 py-2 text-xs font-semibold text-app-on-primary ${pending ? "pointer-events-none opacity-50" : ""}`}>
          انتخاب فایل جدید
          <input type="file" className="hidden" accept={accept} disabled={pending} onChange={event => onChoose(event.target.files?.[0])}/>
        </label>
        {(approvedId || file) && !removed && !pending && <Button size="sm" variant="text" className="text-rose-700" onClick={onRemove}>حذف فایل</Button>}
      </div>
    </div>
  );
}
